import { AttList } from "./SupportAttachments";
import { useState, useEffect, useCallback, useRef } from "react";
import { Megaphone, Search } from "lucide-react";
import { AnnouncementsAdmin } from "./AnnouncementsAdmin";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Building2, CheckCircle2, Clock, XCircle, Loader2, Mail, Hourglass, Users, Briefcase, Wallet, TrendingUp, Palette, Upload, LifeBuoy, Send, ChevronDown, Phone, FileText, MapPin, Globe, Paintbrush, BadgeCheck, Trash2, Save } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ComposedChart, Line,
} from "recharts";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LandingEditorDialog } from "./LandingEditorDialog";
import { SupportTelegramSettings } from "./SupportTelegramSettings";

const STATUS = {
  active: { label: "مشترك", cls: "bg-blue-100 text-blue-700 border-blue-200", icon: CheckCircle2 },
  trial: { label: "تجربة", cls: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  expired: { label: "منتهٍ", cls: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
};

const STATUS_TK = {
  open: { label: "قيد المعالجة", cls: "bg-amber-100 text-amber-700" },
  answered: { label: "تم الرد", cls: "bg-emerald-100 text-emerald-700" },
  closed: { label: "مغلقة", cls: "bg-slate-100 text-slate-500" },
};

const AdminSupportTickets = ({ tickets, openTid, setOpenTid, reply, setReply, ticketBusy, onReply, onStatus }) => {
  const unread = tickets.filter((t) => t.admin_unread).length;
  return (
    <div className="rounded-2xl border-2 border-blue-200 bg-blue-50/50 p-4 space-y-3" data-testid="admin-support-section">
      <div>
        <p className="text-sm font-bold text-blue-900 flex items-center gap-1.5">
          <LifeBuoy className="h-5 w-5 text-blue-600" /> تذاكر الدعم الفني الواردة من الشركات
          {unread > 0 && (
            <span className="rounded-full bg-rose-500 text-white text-[10px] px-1.5 py-0.5" data-testid="admin-tickets-unread">{unread} جديدة</span>
          )}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">هنا تصلك رسائل الدعم من مدراء الشركات. اضغط على أي تذكرة لعرض المحادثة والرد عليها.</p>
      </div>
      <SupportTelegramSettings />
      {tickets.length === 0 ? (
        <p className="text-xs text-slate-400 py-2">لا توجد تذاكر دعم من الشركات بعد.</p>
      ) : (
        <div className="space-y-2" data-testid="admin-tickets-list">
          {tickets.map((tk) => {
            const isOpen = openTid === tk.id;
            const st = STATUS_TK[tk.status] || STATUS_TK.open;
            return (
              <div key={tk.id} data-testid={`admin-ticket-${tk.id}`} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <button type="button" onClick={() => { setOpenTid(isOpen ? null : tk.id); setReply(""); }}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-right hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {tk.admin_unread && <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" />}
                      <span className="font-semibold text-slate-800 text-sm truncate">{tk.subject}</span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">{tk.org_name} · <span dir="ltr">{tk.created_by_email}</span></p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="bg-slate-50/70 border-t border-slate-100 p-3 space-y-2" data-testid={`admin-ticket-body-${tk.id}`}>
                    <div className="max-h-52 overflow-y-auto space-y-2">
                      {(tk.messages || []).map((m) => (
                        <div key={m.id} className={`flex ${m.sender === "support" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "support" ? "bg-blue-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                            <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                            <AttList items={m.attachments} light={m.sender === "support"} />
                            <p className={`text-[9px] mt-1 font-mono-nums ${m.sender === "support" ? "text-blue-100" : "text-slate-400"}`} dir="ltr">{String(m.created_at).slice(0, 16).replace("T", " ")}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-end gap-2">
                      <Textarea data-testid={`admin-reply-input-${tk.id}`} rows={1} value={reply} onChange={(e) => setReply(e.target.value)}
                        placeholder="اكتب ردك للشركة…" className="flex-1 min-h-[40px] max-h-28 resize-none bg-white" />
                      <Button data-testid={`admin-reply-btn-${tk.id}`} onClick={() => onReply(tk.id)} disabled={ticketBusy || !reply.trim()} size="icon" className="bg-blue-600 hover:bg-blue-700 shrink-0">
                        {ticketBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onStatus(tk.id, "closed")} data-testid={`admin-ticket-close-${tk.id}`}>إغلاق التذكرة</Button>
                      {tk.status === "closed" && <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onStatus(tk.id, "open")}>إعادة فتح</Button>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const AdminOrgsDialog = ({ open, onOpenChange, initialTab = "companies", embed = false, visibleTabs = null }) => {
  const { platform, refreshPlatform } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [orgs, setOrgs] = useState([]);
  const [pending, setPending] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [email, setEmail] = useState("");
  const [q, setQ] = useState("");
  const [stF, setStF] = useState("all");
  const [sortBy, setSortBy] = useState("");
  const listRef = useRef(null);
  const pickMetric = (k) => {
    const map = { total_companies: ["all", ""], active_companies: ["active", ""], trial_companies: ["trial", ""], expired_subscriptions: ["expired", ""],
      total_users: ["all", "users"], total_employees: ["all", "employees"], monthly_revenue: ["active", ""], annual_revenue: ["active", ""] };
    const [st, so] = map[k] || ["all", ""];
    setStF(st); setSortBy(so); setQ("");
    setTimeout(() => listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const shownOrgs = (() => {
    const t = q.trim().toLowerCase();
    const filtered = stF === "all" ? orgs : orgs.filter((o) => (o.status || "expired") === stF);
    const base = sortBy ? [...filtered].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0)) : filtered;
    if (!t) return base;
    const d = t.replace(/\D/g, "").replace(/^(00)?966/, "").replace(/^0/, "");
    return base.filter((o) => {
      const txt = [o.name, o.owner_email, o.email, o.phone, o.cr_number, o.search_keys].filter(Boolean).join(" ").toLowerCase();
      if (txt.includes(t)) return true;
      return d.length >= 4 && txt.replace(/\D/g, " ").split(" ").some((n) => n.replace(/^(00)?966/, "").replace(/^0/, "").includes(d));
    });
  })();
  const [months, setMonths] = useState("1");
  const [plan, setPlan] = useState("business");
  const [orgPlan, setOrgPlan] = useState({});
  const [orgMonths, setOrgMonths] = useState({});
  const [openOrg, setOpenOrg] = useState(null);
  const [activating, setActivating] = useState(false);
  const [pName, setPName] = useState("");
  const [pTagline, setPTagline] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoRef = useRef(null);
  const [tickets, setTickets] = useState([]);
  const [openTid, setOpenTid] = useState(null);
  const [reply, setReply] = useState("");
  const [ticketBusy, setTicketBusy] = useState(false);

  const reloadTickets = async () => {
    try { setTickets((await api.adminSupportTickets()).items || []); } catch { /* ignore */ }
  };
  const sendAdminReply = async (tid) => {
    if (!reply.trim()) return;
    setTicketBusy(true);
    try { await api.adminReplyTicket(tid, reply.trim()); setReply(""); await reloadTickets(); toast.success("تم إرسال الرد"); }
    catch { toast.error("تعذّر الإرسال"); } finally { setTicketBusy(false); }
  };
  const setTicketStatus = async (tid, status) => {
    try { await api.adminTicketStatus(tid, status); await reloadTickets(); }
    catch { toast.error("تعذّر التحديث"); }
  };

  useEffect(() => {
    if ((open || embed) && platform) { setPName(platform.name || ""); setPTagline(platform.tagline || ""); }
  }, [open, embed, platform]);

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      await api.updatePlatform({ name: pName.trim(), tagline: pTagline.trim() });
      await refreshPlatform();
      toast.success("تم تحديث هوية المنصة");
    } catch { toast.error("تعذّر الحفظ"); }
    finally { setSavingBrand(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setLogoBusy(true);
    try { await api.uploadPlatformLogo(file); await refreshPlatform(); toast.success("تم تحديث شعار المنصة"); }
    catch (err) { toast.error(err?.response?.data?.detail || "تعذّر رفع الشعار"); }
    finally { setLogoBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, p] = await Promise.all([api.adminOrgs(), api.adminPending()]);
      setOrgs(o); setPending(p);
      try { setMetrics(await api.adminMetrics()); } catch { /* metrics optional */ }
      reloadTickets();
    } catch { toast.error("تعذّر تحميل المؤسسات"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open || embed) load(); }, [open, embed, load]);
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  const ticketsUnread = tickets.filter((t) => t.admin_unread).length;

  const activateByEmail = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.warning("أدخل بريداً صحيحاً"); return; }
    setActivating(true);
    try {
      const res = await api.adminActivateByEmail(e, Number(months), plan);
      toast.success(res.pending ? "لم يسجّل العميل الدخول بعد — سيُفعّل تلقائياً عند أول دخول" : "تم تفعيل الاشتراك");
      setEmail("");
      load();
    } catch { toast.error("تعذّر التفعيل"); }
    finally { setActivating(false); }
  };

  const act = async (orgId, fn, okMsg) => {
    setBusy(orgId);
    try { await fn(); toast.success(okMsg); load(); }
    catch { toast.error("تعذّر التنفيذ"); }
    finally { setBusy(null); }
  };

  const delOrg = async (o) => {
    const msg = `⚠️ حذف نهائي لا يمكن التراجع عنه.\n\nسيتم حذف منشأة «${o.name}» (${o.owner_email}) وكل بياناتها:\nالموظفون (${o.employees}) • المستخدمون • الملفات • السجلات المالية والاشتراك.\n\nاكتب نعم؟ اضغط موافق للحذف النهائي.`;
    if (!window.confirm(msg)) return;
    setBusy(o.org_id);
    try {
      await api.adminDeleteOrg(o.org_id);
      toast.success(`تم حذف منشأة «${o.name}» وكل بياناتها نهائياً`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر حذف المنشأة");
    } finally { setBusy(null); }
  };

  const showTab = (k) => !visibleTabs || visibleTabs.includes(k);
  const hideTabList = embed && Array.isArray(visibleTabs) && visibleTabs.length === 1;
  const consoleBody = (
        <Tabs value={tab} onValueChange={setTab} dir="rtl" className="mt-2">
          {!hideTabList && (
          <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100" data-testid="console-tabs">
            {showTab("companies") && (
            <TabsTrigger value="companies" data-testid="console-tab-companies" className="gap-1.5 text-xs sm:text-sm">
              <Building2 className="h-4 w-4" /> الشركات والاشتراكات
            </TabsTrigger>
            )}
            {showTab("support") && (
            <TabsTrigger value="support" data-testid="console-tab-support" className="gap-1.5 text-xs sm:text-sm">
              <LifeBuoy className="h-4 w-4" /> الدعم الفني
              {ticketsUnread > 0 && <span className="rounded-full bg-rose-500 text-white text-[10px] px-1.5" data-testid="console-tickets-unread">{ticketsUnread}</span>}
            </TabsTrigger>
            )}
            {showTab("identity") && (
            <TabsTrigger value="identity" data-testid="console-tab-identity" className="gap-1.5 text-xs sm:text-sm">
              <BadgeCheck className="h-4 w-4" /> هوية المنصة
            </TabsTrigger>
            )}
            {showTab("announcements") && (
            <TabsTrigger value="announcements" data-testid="console-tab-announcements" className="gap-1.5 text-xs sm:text-sm">
              <Megaphone className="h-4 w-4" /> الإعلانات
            </TabsTrigger>
            )}
            {showTab("design") && (
            <TabsTrigger value="design" data-testid="console-tab-design" className="gap-1.5 text-xs sm:text-sm">
              <Paintbrush className="h-4 w-4" /> تصميم الموقع
            </TabsTrigger>
            )}
          </TabsList>
          )}

          <TabsContent value="companies" className="pt-4 space-y-4">
          {loading ? (
            <div className="py-12 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : (
          <>
            {metrics && (
              <div className="space-y-4" data-testid="admin-metrics">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { k: "total_companies", label: "إجمالي الشركات", icon: Building2, cls: "text-blue-700 bg-blue-50 border-blue-100", v: metrics.total_companies },
                    { k: "active_companies", label: "شركات مشتركة", icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-100", v: metrics.active_companies },
                    { k: "trial_companies", label: "قيد التجربة", icon: Clock, cls: "text-amber-700 bg-amber-50 border-amber-100", v: metrics.trial_companies },
                    { k: "expired_subscriptions", label: "اشتراكات منتهية", icon: XCircle, cls: "text-red-700 bg-red-50 border-red-100", v: metrics.expired_subscriptions },
                    { k: "total_users", label: "إجمالي المستخدمين", icon: Users, cls: "text-slate-700 bg-slate-50 border-slate-100", v: metrics.total_users },
                    { k: "total_employees", label: "إجمالي الموظفين", icon: Briefcase, cls: "text-slate-700 bg-slate-50 border-slate-100", v: metrics.total_employees },
                    { k: "monthly_revenue", label: "الإيراد الشهري", icon: Wallet, cls: "text-blue-700 bg-blue-50 border-blue-100", v: `${metrics.monthly_revenue.toLocaleString("en-US")} ${metrics.currency}` },
                    { k: "annual_revenue", label: "الإيراد السنوي", icon: TrendingUp, cls: "text-emerald-700 bg-emerald-50 border-emerald-100", v: `${metrics.annual_revenue.toLocaleString("en-US")} ${metrics.currency}` },
                  ].map((c) => (
                    <button type="button" key={c.k} data-testid={`metric-${c.k}`} onClick={() => pickMetric(c.k)}
                      className={`rounded-2xl border p-3 text-start transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 ${c.cls}`}>
                      <div className="flex items-center gap-1.5 text-xs opacity-80"><c.icon className="h-3.5 w-3.5" /> {c.label}</div>
                      <div className="mt-1 font-mono-nums font-extrabold text-lg">{c.v}</div>
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-slate-200 bg-white p-3">
                    <p className="text-sm font-semibold text-slate-700 mb-2">نمو الشركات والمستخدمين</p>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={metrics.growth} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2563EB" stopOpacity={0.4} /><stop offset="1" stopColor="#2563EB" stopOpacity={0} /></linearGradient>
                          <linearGradient id="gu" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#10B981" stopOpacity={0.4} /><stop offset="1" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Area type="monotone" dataKey="companies" name="الشركات" stroke="#2563EB" fill="url(#gc)" strokeWidth={2} />
                        <Area type="monotone" dataKey="users" name="المستخدمون" stroke="#10B981" fill="url(#gu)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-3">
                    <p className="text-sm font-semibold text-slate-700 mb-2">توزيع الباقات (المشتركون)</p>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={metrics.plan_distribution} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                        <XAxis dataKey="plan" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" name="عدد الشركات" fill="#2563EB" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {metrics.revenue_growth && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="admin-revenue-chart">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                      <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                        <TrendingUp className="h-4 w-4 text-emerald-600" /> نمو الاشتراكات والإيراد المُحصّل شهرياً
                      </p>
                      <span className="text-[11px] text-slate-400">آخر 6 أشهر · الإيراد بالريال</span>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                      <ComposedChart data={metrics.revenue_growth} margin={{ top: 6, right: 6, left: -14, bottom: 0 }}>
                        <defs>
                          <linearGradient id="grev" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" stopColor="#10B981" stopOpacity={0.9} />
                            <stop offset="1" stopColor="#10B981" stopOpacity={0.35} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="rev" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <YAxis yAxisId="subs" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip formatter={(v, n) => [n === "الإيراد (⃁)" ? `${Number(v).toLocaleString("en-US")} ⃁` : v, n]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar yAxisId="rev" dataKey="revenue" name="الإيراد (⃁)" fill="url(#grev)" radius={[6, 6, 0, 0]} maxBarSize={44} />
                        <Line yAxisId="subs" type="monotone" dataKey="subscriptions" name="اشتراكات جديدة" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4 space-y-3">
              <p className="text-sm font-semibold text-blue-800 flex items-center gap-1.5">
                <Mail className="h-4 w-4" /> تفعيل ببريد مباشرة
              </p>
              <p className="text-xs text-slate-500">فعّل عميلاً ببريده حتى قبل أول دخول له — سيُفعّل تلقائياً عند تسجيله.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input data-testid="activate-email-input" dir="ltr" type="email" placeholder="customer@email.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1" />
                <Select value={months} onValueChange={setMonths}>
                  <SelectTrigger data-testid="activate-months-select" className="w-full sm:w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">شهر</SelectItem>
                    <SelectItem value="3">3 أشهر</SelectItem>
                    <SelectItem value="6">6 أشهر</SelectItem>
                    <SelectItem value="12">سنة</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={plan} onValueChange={setPlan}>
                  <SelectTrigger data-testid="activate-plan-select" className="w-full sm:w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">المبتدئة (99)</SelectItem>
                    <SelectItem value="business">الأعمال (199)</SelectItem>
                    <SelectItem value="professional">الاحترافية (299)</SelectItem>
                    <SelectItem value="enterprise">المؤسسات</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="activate-by-email-button" disabled={activating} onClick={activateByEmail}
                  className="bg-blue-600 hover:bg-blue-700 gap-1.5 shrink-0">
                  {activating && <Loader2 className="h-4 w-4 animate-spin" />} تفعيل
                </Button>
              </div>
              {pending.length > 0 && (
                <div className="pt-1">
                  <p className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1"><Hourglass className="h-3.5 w-3.5" /> بانتظار أول دخول</p>
                  <div className="flex flex-wrap gap-1.5">
                    {pending.map((p) => (
                      <Badge key={p.email} variant="outline" className="text-slate-600 gap-1" data-testid={`pending-${p.email}`}>
                        <span dir="ltr">{p.email}</span> · {p.months} شهر
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative mt-2 scroll-mt-4" ref={listRef}>
              <Search className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input data-testid="orgs-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم الشركة أو الإيميل أو الجوال" className="pr-9" />
              {(q || stF !== "all") && <span className="absolute left-3 top-2.5 text-xs text-slate-400" data-testid="orgs-search-count">{shownOrgs.length} نتيجة</span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="orgs-status-filters">
              {[["all", "الكل", "bg-slate-800 text-white border-slate-800", "text-slate-600 border-slate-200"], ...Object.entries(STATUS).map(([k, v]) => [k, v.label, v.cls.replace(/bg-(\w+)-100/, "bg-$1-600").replace(/text-(\w+)-700/, "text-white"), "text-slate-600 border-slate-200"])].map(([k, label, on, off]) => {
                const n = k === "all" ? orgs.length : orgs.filter((o) => (o.status || "expired") === k).length;
                return (
                  <button key={k} type="button" onClick={() => { setStF(k); setSortBy(""); }} data-testid={`orgs-filter-${k}`}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${stF === k ? on : `bg-white hover:bg-slate-50 ${off}`}`}>
                    {label} <span className="opacity-75">({n})</span>
                  </button>
                );
              })}
            </div>
            {shownOrgs.length === 0 ? (
              <div className="py-12 text-center text-slate-400">لا توجد مؤسسات بعد.</div>
            ) : (
              <div className="space-y-3 py-2">
                {shownOrgs.map((o) => {
                  const s = STATUS[o.status] || STATUS.expired;
                  const Icon = s.icon;
                  const isOpen = openOrg === o.org_id;
                  return (
                    <div key={o.org_id} data-testid={`admin-org-${o.org_id}`}
                      className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <button type="button" onClick={() => setOpenOrg(isOpen ? null : o.org_id)}
                          data-testid={`admin-org-toggle-${o.org_id}`}
                          className="flex items-center gap-3 text-right group flex-1 min-w-0">
                          <div className="h-11 w-11 rounded-xl border bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                            {o.logo_id ? <img src={fileUrl(o.logo_id)} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-5 w-5 text-slate-300" />}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-800 group-hover:text-blue-700 transition flex items-center gap-1.5">
                              {o.name}
                              <ChevronDown className={`h-4 w-4 text-slate-400 transition ${isOpen ? "rotate-180" : ""}`} />
                            </p>
                            <p className="text-xs text-slate-400 truncate" dir="ltr">{o.owner_email}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {o.employees} موظف{sortBy === "users" ? ` · ${o.users || 0} مستخدم` : ""}
                              {o.status === "trial" && ` · تبقّى ${o.days_left} يوم من التجربة`}
                              {o.status === "active" && o.subscribed_until && ` · حتى ${o.subscribed_until.slice(0, 10)}`}
                              {o.status === "active" && !o.subscribed_until && " · اشتراك دائم"}
                            </p>
                          </div>
                        </button>
                        <Badge variant="outline" className={`gap-1 ${s.cls}`}>
                          <Icon className="h-3.5 w-3.5" /> {s.label}
                        </Badge>
                      </div>

                      {isOpen && (
                        <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50/70 p-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5"
                          data-testid={`admin-org-details-${o.org_id}`}>
                          <DetailRow icon={Building2} label="اسم الشركة" value={o.name} />
                          <DetailRow icon={Phone} label="رقم الجوال" value={o.phone} ltr />
                          <DetailRow icon={FileText} label="السجل التجاري" value={o.cr_number} ltr />
                          <DetailRow icon={Mail} label="البريد الإلكتروني" value={o.email || o.owner_email} ltr />
                          <DetailRow icon={Globe} label="الموقع الإلكتروني" value={o.website} ltr />
                          <DetailRow icon={Briefcase} label="المجال / النشاط" value={o.industry} />
                          <DetailRow icon={MapPin} label="العنوان" value={o.address} />
                          <DetailRow icon={Users} label="عدد الموظفين المسجّل" value={o.employee_count} />
                        </div>
                      )}

                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <Select value={orgPlan[o.org_id] || o.plan || "business"} onValueChange={(v) => setOrgPlan((m) => ({ ...m, [o.org_id]: v }))}>
                          <SelectTrigger data-testid={`org-plan-select-${o.org_id}`} className="w-36 h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="starter">المبتدئة (99)</SelectItem>
                            <SelectItem value="business">الأعمال (199)</SelectItem>
                            <SelectItem value="professional">الاحترافية (299)</SelectItem>
                            <SelectItem value="enterprise">المؤسسات</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" data-testid={`save-plan-${o.org_id}`} disabled={busy === o.org_id}
                          className="h-9 gap-1 border-green-600 text-green-700 hover:bg-green-50"
                          onClick={() => act(o.org_id, () => api.adminSetPlan(o.org_id, orgPlan[o.org_id] || o.plan || "business"), "تم حفظ الباقة")}>
                          {busy === o.org_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ
                        </Button>
                        <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white ps-2">
                          <input type="number" min={1} max={60} value={orgMonths[o.org_id] ?? 1} data-testid={`activate-months-${o.org_id}`}
                            onChange={(e) => setOrgMonths((m) => ({ ...m, [o.org_id]: e.target.value }))}
                            className="h-9 w-14 bg-transparent text-center text-sm outline-none" dir="ltr" />
                          <span className="text-xs text-slate-500">شهر</span>
                          <Button size="sm" data-testid={`activate-month-${o.org_id}`} disabled={busy === o.org_id || !(Number(orgMonths[o.org_id] ?? 1) >= 1 && Number(orgMonths[o.org_id] ?? 1) <= 60)}
                            className="h-9 rounded-s-none bg-blue-600 hover:bg-blue-700"
                            onClick={() => { const n = Math.floor(Number(orgMonths[o.org_id] ?? 1)); act(o.org_id, () => api.adminActivate(o.org_id, n, orgPlan[o.org_id] || o.plan || "business"), `تم تفعيل ${n} ${n === 1 ? "شهر" : n <= 10 ? "أشهر" : "شهراً"}`); }}>
                            تفعيل
                          </Button>
                        </div>
                        <Button size="sm" variant="outline" data-testid={`activate-year-${o.org_id}`} disabled={busy === o.org_id}
                          onClick={() => act(o.org_id, () => api.adminActivate(o.org_id, 12, orgPlan[o.org_id] || o.plan || "business"), "تم تفعيل سنة")}>
                          تفعيل سنة
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`disable-${o.org_id}`} disabled={busy === o.org_id}
                          className="text-red-600 hover:bg-red-50 mr-auto"
                          onClick={() => act(o.org_id, () => api.adminDisable(o.org_id), "تم إيقاف الاشتراك")}>
                          إيقاف
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`delete-org-${o.org_id}`} disabled={busy === o.org_id}
                          className="text-red-700 hover:bg-red-100 gap-1"
                          onClick={() => delOrg(o)}>
                          {busy === o.org_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} حذف
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
          )}
          </TabsContent>

          <TabsContent value="support" className="pt-4">
            <AdminSupportTickets tickets={tickets} openTid={openTid} setOpenTid={setOpenTid}
              reply={reply} setReply={setReply} ticketBusy={ticketBusy}
              onReply={sendAdminReply} onStatus={setTicketStatus} />
          </TabsContent>

          <TabsContent value="identity" className="pt-4">
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4 space-y-3" data-testid="platform-brand-section">
              <p className="text-sm font-semibold text-indigo-800 flex items-center gap-1.5">
                <Palette className="h-4 w-4" /> هوية المنصة (الاسم والشعار)
              </p>
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-xl border bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {platform?.logo_id ? <img src={fileUrl(platform.logo_id)} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-6 w-6 text-slate-300" />}
                </div>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="platform-logo-input" />
                <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={logoBusy} data-testid="platform-logo-btn">
                  {logoBusy ? <Loader2 className="h-4 w-4 ml-1 animate-spin" /> : <Upload className="h-4 w-4 ml-1" />} رفع الشعار
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-slate-600">اسم المنصة</label>
                  <Input data-testid="platform-name-input" dir="ltr" value={pName} onChange={(e) => setPName(e.target.value)} placeholder="WorkZone" className="mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">الشعار النصي (Tagline)</label>
                  <Input data-testid="platform-tagline-input" value={pTagline} onChange={(e) => setPTagline(e.target.value)} placeholder="إدارة أذكى • أداء أفضل" className="mt-1" />
                </div>
              </div>
              <Button onClick={saveBrand} disabled={savingBrand} data-testid="platform-save-btn" className="bg-indigo-600 hover:bg-indigo-700 gap-1.5">
                {savingBrand && <Loader2 className="h-4 w-4 animate-spin" />} حفظ هوية المنصة
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="announcements" className="pt-4">
            <AnnouncementsAdmin />
          </TabsContent>

          <TabsContent value="design" className="pt-4">
            <LandingEditorDialog embedded />
          </TabsContent>
        </Tabs>
  );
  if (embed) return <div dir="rtl" className="font-body">{consoleBody}</div>;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl font-body max-h-[88vh] overflow-y-auto" dir="rtl" data-testid="admin-orgs-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" /> مركز إدارة المنصة
          </DialogTitle>
          <DialogDescription className="text-right">
            كل ما يخص مالك المنصة في مكان واحد: هوية المنصة وتصميم الموقع.
          </DialogDescription>
        </DialogHeader>
        {consoleBody}
      </DialogContent>
    </Dialog>
  );
};

const DetailRow = ({ icon: Icon, label, value, ltr }) => (
  <div className="flex items-start gap-2" data-testid={`org-detail-${label}`}>
    <Icon className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="text-[11px] text-slate-400">{label}</p>
      <p className={`text-sm font-medium ${value ? "text-slate-800" : "text-slate-300"} truncate`} dir={ltr ? "ltr" : undefined}>
        {value || "—"}
      </p>
    </div>
  </div>
);

